import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHybridAuth } from '../../contexts/HybridAuthContext';
import { useWhitelabel } from '../../contexts/WhitelabelContext';
import {
  Bars3Icon,
  XMarkIcon,
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  HomeIcon,
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  AdjustmentsHorizontalIcon,
  ServerIcon,
  Cog6ToothIcon,
  CubeIcon,
  QueueListIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

interface SuperAdminLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  id: string;
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  badge?: string;
  external?: boolean; // Add support for external links
}

interface MenuCategory {
  id: string;
  name: string;
  items: MenuItem[];
}

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children }) => {
  const { branding } = useWhitelabel();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertCount, setAlertCount] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useHybridAuth();

  // Fetch alert count for badge
  useEffect(() => {
    const fetchAlertCount = async () => {
      try {
        const response = await apiFetch('/api/admin/alerts/summary', {
          headers: {
            'X-Tenant-ID': '1'
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAlertCount(data.total_alerts > 0 ? data.total_alerts.toString() : '');
        }
      } catch (error) {
        console.error('Failed to fetch alert count:', error);
        setAlertCount('');
      }
    };

    fetchAlertCount();
    // Refresh alert count every 30 seconds
    const interval = setInterval(fetchAlertCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const menuCategories: MenuCategory[] = [
    {
      id: 'overview',
      name: 'Overview',
      items: [
        {
          id: 'system-overview',
          name: 'System Overview',
          href: '/admin',
          icon: HomeIcon
        }
      ]
    },
    {
      id: 'tenant-management',
      name: 'Tenant Management',
      items: [
        {
          id: 'tenants',
          name: 'Tenants',
          href: '/admin/tenants',
          icon: UsersIcon
        },
        {
          id: 'projects',
          name: 'Projects',
          href: '/admin/projects',
          icon: GlobeAltIcon
        },
        {
          id: 'super-admins',
          name: 'Super Admins',
          href: '/admin/super-admins',
          icon: UserGroupIcon
        }
      ]
    },
    {
      id: 'system-settings',
      name: 'System Settings',
      items: [
        {
          id: 'general-settings',
          name: 'General Settings',
          href: '/admin/general-settings',
          icon: Cog6ToothIcon
        },
        {
          id: 'system-alerts',
          name: 'System Alerts',
          href: '/admin/system-alerts',
          icon: ExclamationTriangleIcon,
          badge: alertCount
        },
        {
          id: 'system-services',
          name: 'System Services',
          href: '/admin/system-services',
          icon: ServerIcon
        },
        {
          id: 'horizon-jobs',
          name: 'Horizon Jobs',
          href: '/horizon/dashboard',
          icon: QueueListIcon,
          external: true
        },
        {
          id: 'performance',
          name: 'Performance',
          href: '/admin/performance',
          icon: ChartBarIcon
        }
      ]
    },
    {
      id: 'analysis-settings',
      name: 'Analysis Settings',
      items: [
        {
          id: 'seo-scoring',
          name: 'SEO Scoring',
          href: '/admin/seo-scoring',
          icon: AdjustmentsHorizontalIcon
        },
        {
          id: 'general-analysis-scoring',
          name: 'General Analysis Scoring',
          href: '/admin/general-analysis-scoring',
          icon: AdjustmentsHorizontalIcon
        }
      ]
    },
    {
      id: 'revenue',
      name: 'Revenue',
      items: [
        {
          id: 'revenue-analytics',
          name: 'Revenue',
          href: '/admin/revenue',
          icon: CurrencyDollarIcon
        },
        {
          id: 'products',
          name: 'Products',
          href: '/admin/products',
          icon: CubeIcon
        }
      ]
    }
  ];

  const isActiveRoute = (href: string): boolean => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  const handleNavigation = (href: string, external?: boolean) => {
    if (external) {
      // Open external links in the same tab (Horizon)
      window.location.href = href;
    } else {
      navigate(href);
    }
    setSidebarOpen(false);
  };

  const handleBackToApp = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/sign-in');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-900" style={{background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'}}>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div className={`fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setSidebarOpen(false)} />
        <div className={`fixed inset-y-0 left-0 w-64 bg-slate-800/95 backdrop-blur-sm border-r border-slate-700 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-700">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/img/optimancer_logo.svg"
                alt={branding.companyName}
                className="w-8 h-8"
              />
              <span className="text-lg font-bold text-white">Super Admin</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="text-slate-400 hover:text-white">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-6">
            {menuCategories.map((category) => (
              <div key={category.id}>
                <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">
                  {category.name}
                </h3>
                <div className="space-y-1">
                  {category.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.href, item.external)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                        isActiveRoute(item.href) && !item.external
                          ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                          : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <item.icon className="w-5 h-5" />
                        <span>{item.name}</span>
                      </div>
                      {item.badge && (
                        <span className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-300 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          
          {/* Footer with Back to App and Log Out */}
          <div className="p-4 border-t border-slate-700 space-y-2">
            <button
              onClick={handleBackToApp}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700/50 hover:text-white rounded-xl transition-all duration-200"
            >
              <ArrowLeftOnRectangleIcon className="w-5 h-5" />
              <span>Back to App</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-xl transition-all duration-200"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:block">
        <div className="flex flex-col h-full bg-slate-800/95 backdrop-blur-sm border-r border-slate-700">
          <div className="flex items-center h-16 px-6 border-b border-slate-700">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/img/optimancer_logo.svg"
                alt={branding.companyName}
                className="w-8 h-8"
              />
              <span className="text-lg font-bold text-white">Super Admin</span>
            </div>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
            {menuCategories.map((category) => (
              <div key={category.id}>
                <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">
                  {category.name}
                </h3>
                <div className="space-y-1">
                  {category.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.href, item.external)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                        isActiveRoute(item.href) && !item.external
                          ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                          : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <item.icon className="w-5 h-5" />
                        <span>{item.name}</span>
                      </div>
                      {item.badge && (
                        <span className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-300 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-slate-700 space-y-2">
            <button
              onClick={handleBackToApp}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700/50 hover:text-white rounded-xl transition-all duration-200"
            >
              <ArrowLeftOnRectangleIcon className="w-5 h-5" />
              <span>Back to App</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-xl transition-all duration-200"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 lg:pl-64 flex flex-col min-h-screen">
        {/* Top header */}
        <div className="sticky top-0 z-40 bg-slate-800/95 backdrop-blur-sm border-b border-slate-700">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-slate-400 hover:text-white"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>
              <div className="ml-4 lg:ml-0 flex items-center space-x-3">
                <img
                  src="/assets/img/optimancer_logo.svg"
                  alt={branding.companyName}
                  className="w-6 h-6 lg:hidden"
                />
                <h1 className="text-xl font-semibold text-white">
                  {`${branding.companyName} Super Admin Portal`}
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Admin user info */}
              <div className="text-sm text-slate-300">
                <div className="font-medium text-white">Super Admin</div>
                <div className="text-slate-400">admin@optimancer.com</div>
              </div>
              <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">SA</span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content - grows to fill available space */}
        <main className="flex-1 overflow-auto">
          <div className="p-8">
            {children}
          </div>
        </main>

        {/* Footer - sticks to bottom */}
        <footer className="bg-slate-800/50 backdrop-blur-sm border-t border-slate-700 mt-auto">
          <div className="px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-400">
                {`© 2025 ${branding.companyName}. Super Admin Portal v1.0`}
              </div>
              <div className="flex items-center space-x-6 text-sm text-slate-400">
                <span>System Status: <span className="text-emerald-400 font-medium">Operational</span></span>
                <span>Last Updated: {new Date().toLocaleTimeString()}</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
